import {
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

interface UseAutoScrollOptions {
  messagesContainerRef: RefObject<HTMLDivElement>;
  /** Any array of objects that have a `role` field — accepts both BagelChatMessage and UIMessage. */
  messages: { role: string }[];
}

interface UseAutoScrollReturn {
  /** True when the scroll position is within NEAR_BOTTOM_THRESHOLD of the bottom. */
  isAtBottom: boolean;
  /** Programmatically smooth-scroll to the bottom. */
  scrollToBottom: () => void;
}

/**
 * useAutoScroll
 *
 * - Always jumps to the bottom when the user sends a message (new user message
 *   appended to the end of `messages`), regardless of prior scroll position.
 * - Follows along during AI streaming (new tool-call pills, growing text) as long as the
 *   viewer was already at the bottom — so new activity always lands in view instead of
 *   silently appearing below the fold. Never yanks the view if they scrolled up to read
 *   earlier history; the down-chevron overlay (rendered by the parent when !isAtBottom)
 *   is the way back to the bottom in that case.
 * - Exposes `isAtBottom` so the parent can decide when to show the chevron.
 * - Exposes `scrollToBottom` for the chevron button onClick.
 */
export default function useAutoScroll({
  messagesContainerRef,
  messages,
}: UseAutoScrollOptions): UseAutoScrollReturn {
  const NEAR_BOTTOM_THRESHOLD = 60;
  const [isAtBottom, setIsAtBottom] = useState(true);
  // Mirrors `isAtBottom` for synchronous reads inside the messages-effect below — the scroll
  // listener effect runs first each commit, so this is always fresh by the time it's read.
  const isAtBottomRef = useRef(true);
  const prevLengthRef = useRef(messages.length);

  const scrollToBottom = useCallback(
    (behavior: ScrollBehavior = 'smooth') => {
      const container = messagesContainerRef.current;
      if (!container) return;
      container.scrollTo({ top: container.scrollHeight, behavior });
    },
    [messagesContainerRef],
  );

  const markAtBottom = useCallback((atBottom: boolean) => {
    isAtBottomRef.current = atBottom;
    setIsAtBottom(atBottom);
  }, []);

  // --- Track whether the viewport is near the bottom ---
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return undefined;

    const check = () => {
      const dist =
        container.scrollHeight -
        container.scrollTop -
        container.clientHeight;
      markAtBottom(dist <= NEAR_BOTTOM_THRESHOLD);
    };

    check(); // initial check
    container.addEventListener('scroll', check, { passive: true });
    return () => container.removeEventListener('scroll', check);
  }, [messagesContainerRef, markAtBottom]);

  // --- Follow new content to the bottom ---
  // Fires on every `messages` change, including in-place streaming updates (new tool-call
  // parts, growing assistant text) since the caller recomputes that array per chunk.
  // Unconditional for: the initial population from empty (session restore — the container's
  // real scroll position at that point is not reliable enough to gate on) and a newly-sent
  // user message. Otherwise only follows along if the viewer was already at the bottom.
  useEffect(() => {
    const prevLen = prevLengthRef.current;
    prevLengthRef.current = messages.length;

    if (messages.length === 0) return; // nothing to scroll to yet

    const isInitialLoad = prevLen === 0;
    const appendedUserMessage =
      messages.length > prevLen && messages[messages.length - 1]?.role === 'user';

    if (isInitialLoad || appendedUserMessage || isAtBottomRef.current) {
      scrollToBottom('auto');
      markAtBottom(true);
    }
  }, [messages, scrollToBottom, markAtBottom]);

  return { isAtBottom, scrollToBottom };
}
